<template>
  <div>
    <vue-element-loading
      :active="showCardLoading && !preventAppLoading"
      color="rgba(var(--vs-primary), 1)"
      spinner="mini-spinner"/>

    <slot name="title">
      <h2 class="mb-5">
        {{ $t('Title') }}
      </h2>
      <p class="text-grey-dark mb-6">
        {{ $t('CompanyInfoSubtitle') }}
      </p>
    </slot>

    <form @submit.prevent>
      <div class="vx-row">
        <div class="vx-col w-full md:w-1/2">
          <label class="vs-input--label required">
            {{ $tc('$General.BusinessEmail') }}
            <vx-tooltip
              :text="$t('BusinessEmailHelpText')"
              position="top"
              class="inline-block"
              :allow-touch="true">
              <feather-icon
                icon="HelpCircleIcon"
                svgClasses="h-4 w-4 hover:text-primary cursor-pointer ml-1"/>
            </vx-tooltip>
          </label>
          <vs-input
            v-model="model.email"
            class="w-full required"
            :name="$tc('$General.BusinessEmail')"
            type="email"
            v-validate="'required|email'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-mail"
            icon-pack="feather"
            :danger="errors.has($tc('$General.BusinessEmail'))"
            :danger-text="errors.first($tc('$General.BusinessEmail'))"
            val-icon-danger="clear"/>
          <div class="mt-2">
            <a
              v-if="userEmail && userEmail !== model.email"
              href="#"
              class="link-plain"
              @click.prevent="useAccountEmail">
              {{ $t('UseAccountEmailText') }}
            </a>
          </div>
        </div>
        <div class="vx-col w-full md:w-1/2 mt-6 md:mt-0">
          <vs-input
            v-model="model.phone"
            class="w-full required"
            :label="$tc('$General.PhoneNumber')"
            :name="$tc('$General.PhoneNumber')"
            v-validate="'required|max:50'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-phone"
            icon-pack="feather"
            :danger="errors.has($tc('$General.PhoneNumber'))"
            :danger-text="errors.first($tc('$General.PhoneNumber'))"
            val-icon-danger="clear"/>
        </div>
      </div>

      <div class="vx-row mt-6">
        <div class="vx-col w-full md:w-1/2">
          <vs-input
            v-model="model.name"
            class="w-full required"
            :label="$tc('$General.BusinessName')"
            :name="$tc('$General.BusinessName')"
            v-validate="'max:200|required'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-user"
            icon-pack="feather"
            :danger="errors.has($tc('$General.BusinessName'))"
            :danger-text="errors.first($tc('$General.BusinessName'))"
            val-icon-danger="clear"/>
        </div>
        <div
          v-if="selectedCountry"
          class="vx-col w-full md:w-1/2 mt-6 md:mt-0">
          <div class="flex justify-start items-center">
            <label class="vs-input--label">{{ getTaxInfoDescription(selectedCountry) }}</label>
            <tax-id-verification
              v-if="taxIdIsValid"
              class="ml-2"
              :status="taxId.verification.status"
              :description="getTaxInfoDescription(selectedCountry)"
              :name="taxId.verification.verified_name"
              :address="taxId.verification.verified_address"/>
          </div>
          <vs-input
            v-model="model.taxId.value"
            class="w-full"
            :placeholder="getTaxInfoExample(selectedCountry)"
            :name="getTaxInfoDescription(selectedCountry)"
            v-validate="'max:50'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-hash"
            icon-pack="feather"
            :danger="showTaxIdError"
            :danger-text="taxIdErrorText"
            val-icon-danger="clear"
            @input="taxIdError = ''"
          />
        </div>
      </div>

      <div class="vx-row mt-6">
        <div class="vx-col w-full md:w-1/2">
          <vs-input
            v-model="model.website"
            class="w-full"
            :label="`${$t('$General.Website')}`"
            :name="$t('$General.Website')"
            v-validate="'max:500|url'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-link-2"
            icon-pack="feather"
            :danger="errors.has($t('$General.Website'))"
            :danger-text="errors.first($t('$General.Website'))"
            val-icon-danger="clear"/>
        </div>
        <div class="vx-col w-full md:w-1/2 mt-6 md:mt-0">
          <vs-input
            :value="model.timeZone"
            class="w-full required"
            type="hidden"
            :label="$tc('$General.TimeZone')"
            :name="$tc('$General.TimeZone')"
            v-validate="'required'"
            data-vv-validate-on="blur|input|change"/>
          <timezone-select
            v-model="model.timeZone"
            :border-danger="errors.has($tc('$General.TimeZone'))"
            :clearable="false"/>
          <span
            v-show="errors.has($tc('$General.TimeZone'))"
            class="text-danger text-sm">
            {{ errors.first($tc('$General.TimeZone')) }}
          </span>
        </div>
      </div>

      <div class="vx-row mt-6">
        <div class="vx-col w-full md:w-1/2">
          <vs-input
            v-model="model.address"
            class="w-full required"
            :label="$t('$General.AddressLine1')"
            :name="$t('$General.AddressLine1')"
            v-validate="'required|max:500'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-map"
            icon-pack="feather"
            :danger="errors.has($t('$General.AddressLine1'))"
            :danger-text="errors.first($t('$General.AddressLine1'))"
            val-icon-danger="clear"/>
        </div>
        <div class="vx-col w-full md:w-1/2 mt-6 md:mt-0">
          <vs-input
            v-model="model.address2"
            class="w-full"
            :label="`${$t('$General.AddressLine2')}`"
            :placeholder="$t('AddressLine2HelpText')"
            :name="$t('$General.AddressLine2')"
            v-validate="'max:500'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-map"
            icon-pack="feather"
            :danger="errors.has($t('$General.AddressLine2'))"
            :danger-text="errors.first($t('$General.AddressLine2'))"
            val-icon-danger="clear"/>
        </div>
      </div>

      <div class="vx-row mt-6">
        <div class="vx-col w-full md:w-1/2 input-country-edit">
          <vs-input
            v-model="model.country"
            class="w-full required"
            type="hidden"
            :label="$tc('$General.Country')"
            :name="$tc('$General.Country')"
            v-validate="'required|max:200'"
            data-vv-validate-on="blur|input|change"/>
          <v-select
            ref="vSelectCountries"
            class="w-full"
            :class="{'border-danger': errors.has($tc('$General.Country'))}"
            label="name"
            v-model="selectedCountry"
            :multiple="false"
            :closeOnSelect="true"
            :options="countryOptions"
            :clearable="false"
            @input="selectedCountryInput()">
          </v-select>
          <div
            v-show="errors.has($tc('$General.Country'))"
            class="con-text-validation span-text-validation-danger vs-input--text-validation-span v-enter-to"
            style="height: 19px;">
                <span class="span-text-validation">
                {{ errors.first($tc('$General.Country')) }}
                </span>
          </div>
        </div>
        <div class="vx-col w-full md:w-1/2 mt-6 md:mt-0">
          <vs-input
            v-model="model.state"
            class="w-full required"
            :label="$tc('$General.State')"
            :name="$tc('$General.State')"
            v-validate="'required|max:200'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-map"
            icon-pack="feather"
            :danger="errors.has($tc('$General.State'))"
            :danger-text="errors.first($tc('$General.State'))"
            val-icon-danger="clear"/>
        </div>
      </div>

      <div class="vx-row mt-6">
        <div class="vx-col w-full md:w-1/2">
          <vs-input
            v-model="model.city"
            class="w-full required"
            :label="$tc('$General.City/Suburb')"
            :name="$tc('$General.City/Suburb')"
            v-validate="'required|max:200'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-map"
            icon-pack="feather"
            :danger="errors.has($tc('$General.City/Suburb'))"
            :danger-text="errors.first($tc('$General.City/Suburb'))"
            val-icon-danger="clear"/>
        </div>
        <div class="vx-col w-full md:w-1/2 mt-6 md:mt-0">
          <vs-input
            v-model="model.postCode"
            class="w-full required"
            :label="$t('$General.ZipPostalCode')"
            :name="$t('$General.ZipPostalCode')"
            v-validate="'required|max:10'"
            data-vv-validate-on="blur|input"
            icon-no-border
            icon="icon icon-hash"
            icon-pack="feather"
            :danger="errors.has($t('$General.ZipPostalCode'))"
            :danger-text="errors.first($t('$General.ZipPostalCode'))"
            val-icon-danger="clear"/>
        </div>
      </div>

      <slot
        v-if="!asWizardStep"
        name="buttons-wrapper"
        v-bind:save="saveAccountDetails"
        v-bind:cancel="setModel"
        v-bind:formIsChanged="formIsChanged">
        <div class="flex items-center justify-start mt-base">
          <slot
            name="buttons"
            v-bind:save="saveAccountDetails"
            v-bind:cancel="setModel"
            v-bind:formIsChanged="formIsChanged">
            <vs-button
              class="w-full md:w-auto"
              @click="saveAccountDetails()">
              {{ $t('$General.Update') }}
            </vs-button>

            <vs-button
              v-show="formIsChanged"
              class="w-full md:w-auto ml-3 vs-button-cancel-flat"
              color="danger"
              @click="setModel()">
              {{ $t('$General.Cancel') }}
            </vs-button>
          </slot>

        </div>
      </slot>
    </form>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import vSelect from 'vue-select';

// utils
import allCountries from '@/assets/utils/all-countries';

// components
import UserAccountBillingInformationTaxIdVerification
from '@/views/modules/user/user-account/user-account-details/UserAccountDetailsCompanyInformationTaxIdVerification.vue';
import TimezoneSelect from '@/views/modules/_components/TimezoneSelect.vue';

// services
import tenantService from '@/api/modules/tenant.service';

/**
 * Component for allow the user view and edit his account details information
 *
 * @module views/modules/user-account/UserAccountSettingsDetails
 * @author Dilan Useche <dilan8810@gmail.com>
 */
export default {
  name: 'UserAccountDetailsCompanyInformation',
  i18n: {
    messages: {
      en: {
        Title: 'Business information',
        UpdatedSuccessNotifyTitle: 'Updated account details',
        UpdatedSuccessNotifyMsg: 'Account details updated successfully',
        DefaultTaxInfoDescription: 'Business tax ID',
        taxIdErrorText: 'Invalid business tax ID',
        BusinessEmailHelpText: 'This email appears on company policies and business communications. It can be different from your account login email.',
        UseAccountEmailText: 'Use account email',
        CompanyInfoSubtitle: 'This section is for your business details and will appear on customer-facing tools and documents.',
        AddressLine2HelpText: 'Optional. Suite, apartment, floor, or unit number.',
      },
    },
  },
  components: {
    vSelect,
    taxIdVerification: UserAccountBillingInformationTaxIdVerification,
    TimezoneSelect,
  },
  props: {
    onModal: {
      type: Boolean,
      require: false,
      default: false,
    },
    asWizardStep: {
      type: Boolean,
      require: false,
      default: false,
    },
  },
  data() {
    return {
      payload: null,
      model: {
        email: '',
        phone: '',
        name: '',
        country: '',
        state: '',
        city: '',
        postCode: '',
        address: '',
        address2: '',
        timeZone: '',
        website: '',
        taxId: {
          type: '',
          value: '',
        },
      },
      taxId: null,
      taxIdError: '',
      selectedCountry: null,
      countryOptions: allCountries,
      showCardLoading: false,
    };
  },
  computed: {
    formIsChanged() {
      return Object.keys(this.validationFields).some((key) => this.validationFields[key].changed);
    },
    taxIdIsValid() {
      return this.taxId && this.taxId.value && this.taxId.type
        && this.taxId.verification && this.taxId.verification.status
        && this.taxId.type === this.selectedCountry.taxInfo.type
        && this.taxId.value === this.model.taxId.value;
    },
    showTaxIdError() {
      return this.errors.has(this.getTaxInfoDescription(this.selectedCountry))
        || !!this.taxIdError;
    },
    taxIdErrorText() {
      return this.errors.first(this.getTaxInfoDescription(this.selectedCountry))
        || this.$t('taxIdErrorText');
    },
    userEmail() {
      return this.$store.state.auth.user ? this.$store.state.auth.user.email : '';
    },
  },
  watch: {
    selectedCountry(val) {
      this.model.country = val ? val.iso2 : null;
    },
    'model.taxId.value': function (val) {
      if (val) {
        this.model.taxId.value = val.replace(/ /g, '');
      }
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    ...mapActions({
      updateTenantAccountDetailsSettings: 'auth/updateTenantAccountDetailsSettings',
    }),
    getTaxInfoDescription(country) {
      return country.taxInfo ? country.taxInfo.description : this.$t('DefaultTaxInfoDescription');
    },
    getTaxInfoExample(country) {
      return country.taxInfo ? country.taxInfo.example : '123456789';
    },
    async fetchData() {
      this.showCardLoading = true;
      await this.fetchSettingDetails();
      this.showCardLoading = false;
    },
    async fetchSettingDetails() {
      const resp = await tenantService.getAccountSettingsDetails();

      this.payload = resp.data.detailsInformation;
      this.taxId = resp.data.taxId;
      this.setModel();
    },
    setModel() {
      this.model.name = this.payload.name || '';
      this.model.email = this.payload.email || this.userEmail || '';
      this.model.phone = this.payload.phone || '';
      this.model.country = this.payload.country || '';
      this.model.state = this.payload.state || '';
      this.model.city = this.payload.city || '';
      this.model.postCode = this.payload.postCode || '';
      this.model.address = this.payload.address || '';
      this.model.address2 = this.payload.address2 || '';
      this.model.timeZone = this.payload.timeZone || this.model.timeZone;
      this.model.website = this.payload.website || this.model.website;
      this.model.taxId.type = this.payload.taxId
        ? this.payload.taxId.type || ''
        : '';
      this.model.taxId.value = this.payload.taxId
        ? this.payload.taxId.value || ''
        : '';
      this.selectedCountry = this.countryOptions.find(
        (country) => country.iso2 === this.payload.country,
      ) || null;

      this.$validator.reset();
    },
    async saveAccountDetails() {
      const formIsValid = await this.$validator.validate();

      if (!formIsValid) {
        document.getElementsByName([this.$validator.errors.items[0].field])[0].focus();
        return false;
      }

      try {
        this.showCardLoading = true;
        const resp = await this.updateTenantAccountDetailsSettings(this.model);
        this.payload = resp.detailsInformation;
        this.taxId = resp.taxId;

        this.$showSuccessActionNotification({
          title: this.$t('UpdatedSuccessNotifyTitle'),
          text: this.$t('UpdatedSuccessNotifyMsg'),
        });

        this.setModel();
        return true;
      } catch (err) {
        if (err.response && err.response.data && err.response.data.code === 'tax_id_invalid') {
          this.taxIdError = err.response.data.code;
        }

        return false;
      } finally {
        this.showCardLoading = false;
      }
    },
    selectedCountryInput() {
      this.model.taxId.type = this.selectedCountry && this.selectedCountry.taxInfo
        ? this.selectedCountry.taxInfo.type : '';
              this.model.taxId.value = '';
      },
    async useAccountEmail() {
      // Set the email from the user account
      this.model.email = this.userEmail;
      
      // Wait for Vue's reactivity to update validation
      await this.$nextTick();
      
      // Automatically save the form
      await this.saveAccountDetails();
    },
    },
};
</script>
